import { Flooding } from 'features/flooding/flooding.types';
import { GeoEventType } from 'features/geoEvents/geoEvent.types';
import { getMatchingRegions } from 'features/geoEvents/geoEvent.utils';
import { GeoEventRegion } from 'features/geoEvents/geoEventRegion.types';
import { Hazard } from 'features/hazards/hazard.types';
import { Location } from 'features/locations/location.types';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { useQueryParams } from 'hooks/useQueryParams';
import { attemptAsync } from 'es-toolkit/util';
import { useCallback, useEffect, useRef } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { MapLocation } from 'shared/types';
import { parseLatitudeValue, parseLongitudeValue } from 'shared/utils';

type QueryParams = {
  lat?: string | null;
  lng?: string | null;
  address?: string | null;
};

export type FormValues = {
  lat: number;
  lng: number;
  address?: string;
  regions: GeoEventRegion[];
};

export type UseSyncInitialLocationToFormStateProps<T extends FormValues> = {
  isLoadingRegions: boolean;
  regionOptions: GeoEventRegion[];
  geoEventType: GeoEventType;
  geoEvent?: Wildfire | Location | Flooding | Hazard;
  canUpdateAddressAndRegion?: boolean;
  setValue: UseFormSetValue<T>;
};

type HandleLocationChange = (
  locationUpdate: MapLocation | { lat: number; lng: number } | null,
  addressUpdate?: string | null,
  isSync?: boolean,
) => Promise<void>;

type UseSyncInitialLocationToFormState = {
  handleLocationChange: HandleLocationChange;
};

type GetFinalAddressArgs = {
  addressUpdate?: string | null;
  geoEvent?: Wildfire | Location | Flooding | Hazard;
  locationUpdate: MapLocation | { lat: number; lng: number };
  placeName: string;
};

export const getFinalAddress = ({
  addressUpdate,
  geoEvent,
  locationUpdate,
  placeName,
}: GetFinalAddressArgs): string | null => {
  if (addressUpdate) {
    return addressUpdate;
  }

  if (geoEvent) {
    return null;
  }

  if (
    'formattedAddress' in locationUpdate &&
    locationUpdate.type === 'Feature'
  ) {
    return (
      locationUpdate.formattedAddress?.trim() || locationUpdate.name || null
    );
  }

  return placeName || null;
};

export const useSyncInitialLocationToFormState = <T extends FormValues>(
  props: UseSyncInitialLocationToFormStateProps<T>,
): UseSyncInitialLocationToFormState => {
  const {
    isLoadingRegions,
    regionOptions,
    geoEventType,
    geoEvent,
    canUpdateAddressAndRegion = true,
    setValue,
  } = props;

  const [queryParams] = useQueryParams<QueryParams>();
  const initialLocationChangeRef = useRef(false);

  const handleLocationChange: HandleLocationChange = useCallback(
    async (locationUpdate, addressUpdate, isSync) => {
      if (!locationUpdate) return;

      const shouldDirty = !isSync;
      const shouldValidate = !isSync;

      // @ts-expect-error react-hook-form generic limitation
      setValue('lat', locationUpdate.lat, { shouldDirty });
      // @ts-expect-error react-hook-form generic limitation
      setValue('lng', locationUpdate.lng, { shouldDirty });

      if (!canUpdateAddressAndRegion) return;

      const [, result] = await attemptAsync(() =>
        getMatchingRegions(
          locationUpdate.lat,
          locationUpdate.lng,
          regionOptions,
          geoEventType,
        ),
      );

      if (!result) return;

      const { placeName, matchingRegions } = result;

      const finalAddress = getFinalAddress({
        addressUpdate,
        geoEvent,
        locationUpdate,
        placeName,
      });

      if (finalAddress) {
        // @ts-expect-error react-hook-form generic limitation
        setValue('address', finalAddress, { shouldDirty, shouldValidate });
      }

      setValue(
        // @ts-expect-error react-hook-form generic limitation
        'regions',
        matchingRegions.map((region) => ({
          id: region.id,
          displayName: region.displayName,
          state: region.state,
          name: region.name,
          evacZoneStyle: region.evacZoneStyle,
        })),
        { shouldDirty, shouldValidate },
      );
    },
    [
      canUpdateAddressAndRegion,
      geoEvent,
      geoEventType,
      regionOptions,
      setValue,
    ],
  );

  /**
   * Use the initial lat, lng, address from query params to prefill address
   * information when the form first loads.
   */
  useEffect(() => {
    const initialLatitude = parseLatitudeValue(queryParams.lat);
    const initialLongitude = parseLongitudeValue(queryParams.lng);

    if (
      initialLocationChangeRef.current ||
      !initialLatitude ||
      !initialLongitude ||
      isLoadingRegions
    ) {
      return;
    }

    const initialAddress = decodeURIComponent(queryParams.address ?? '');
    handleLocationChange(
      { lat: initialLatitude, lng: initialLongitude },
      initialAddress,
      true,
    );
    initialLocationChangeRef.current = true;
  }, [
    handleLocationChange,
    isLoadingRegions,
    queryParams.lat,
    queryParams.lng,
    queryParams.address,
  ]);

  return { handleLocationChange };
};
